import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';

import { translateSignal, TranslocoPipe } from '@jsverse/transloco';
import {
  SearchModalItem,
  SearchSuggestion,
  SkProgressComponent,
  SkReviewChangesDialogComponent,
  SkSearchModalComponent,
  SkTourOverlayComponent,
  SkWhatsNewService,
} from '@mercadoni/stark-ui';

import type { ServerResponseData, UserMenuAction, UserRole } from '../../core/interfaces';
import { AuthService, FavoritesService, GlobalSearchService, HalloweenService, LayoutStateService, LeapBotChatService, NotificationsService, PINNED_SIDEBAR_TOOL, ReviewChangesService, RouteMetadataService, ThemeService, ToolRegistryService } from '../../core/services';
import { AdminHubModalComponent } from '../../features/admin/admin-hub-modal/admin-hub-modal.component';
import { NfWhatsNewModalComponent, WhatsNewVisibilityService } from '../../onboarding';
import { NfHalloweenOverlayComponent } from '../../shared/components/nf-halloween/halloween-overlay.component';
import { NfLanguageDialogComponent } from '../../shared/components/nf-language-dialog/nf-language-dialog.component';
import { NfServerResponseModalComponent } from '../../shared/components/nf-server-response-modal/nf-server-response-modal.component';
import { captureFocus } from '../../shared/utils/focus-return.util';
import { redactSensitive } from '../../shared/utils/redact-sensitive.util';
import { notificationToResponse } from '../../shared/utils/server-response.util';
import { toolPathSegment } from '../../shared/utils/tool-path.util';
import { NfLeapBotChatComponent } from '../leapbot-chat/leapbot-chat.component';
import { NfLeapBotFabComponent } from '../leapbot-fab/leapbot-fab.component';
import { NfNotificationsDrawerComponent } from '../notifications-drawer/notifications-drawer.component';
import { NfSidebarComponent } from '../sidebar/sidebar.component';
import { NfTopbarComponent } from '../topbar/topbar.component';

@Component({
  selector: 'nf-shell',
  imports: [
    TranslocoPipe,
    RouterOutlet,
    AdminHubModalComponent,
    NfTopbarComponent,
    NfSidebarComponent,
    NfLanguageDialogComponent,
    NfLeapBotChatComponent,
    NfLeapBotFabComponent,
    NfNotificationsDrawerComponent,
    NfServerResponseModalComponent,
    SkProgressComponent,
    SkReviewChangesDialogComponent,
    SkSearchModalComponent,
    SkTourOverlayComponent,
    NfWhatsNewModalComponent,
    NfHalloweenOverlayComponent,
  ],
  templateUrl: './shell.component.html',
  host: {
    '(document:keydown)': 'onGlobalKeydown($event)',
    '(window:resize)': 'onViewportResize()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent {
  private readonly routeMetadata = inject(RouteMetadataService);
  private readonly auth = inject(AuthService);
  private readonly favorites = inject(FavoritesService);
  private readonly router = inject(Router);
  readonly search = inject(GlobalSearchService);

  private readonly toolRegistry = inject(ToolRegistryService);
  readonly notifications = inject(NotificationsService);
  readonly reviewChanges = inject(ReviewChangesService);
  readonly theme = inject(ThemeService);
  readonly halloween = inject(HalloweenService);
  readonly chatOpen = signal(false);
  readonly languageOpen = signal(false);
  readonly leapbot = inject(LeapBotChatService);
  private restoreChatFocus: (() => void) | null = null;
  readonly layout = inject(LayoutStateService);
  private viewportIsCompact = window.innerWidth < 1024;
  private sidebarExpandedStateBeforeCompact: boolean | null = null;
  private readonly whatsNew = inject(SkWhatsNewService);
  /** Instantiated here so the visible catalog is handed to the What's New engine once the shell is up. */
  private readonly whatsNewVisibility = inject(WhatsNewVisibilityService);

  private readonly tServerResponse = translateSignal('layout.shell.serverResponse');
  private readonly tSearchTitle = translateSignal('layout.search.title');
  private readonly tSearchSubtitle = translateSignal('layout.search.subtitle');
  private readonly tSearchSubtitleCategory = translateSignal('layout.search.subtitleCategory');
  private readonly tSearchPlaceholder = translateSignal('layout.search.placeholder');
  private readonly tSearchPlaceholderCategory = translateSignal('layout.search.placeholderCategory');
  private readonly tResult = translateSignal('layout.search.result');
  private readonly tResultPlural = translateSignal('layout.search.resultPlural');
  private readonly tResultTool = translateSignal('layout.search.resultTool');
  private readonly tResultToolPlural = translateSignal('layout.search.resultToolPlural');
  private readonly tEmptyTitle = translateSignal('layout.search.emptyTitle');
  private readonly tEmptyTitleCategory = translateSignal('layout.search.emptyTitleCategory');
  private readonly tEmptyDescription = translateSignal('layout.search.emptyDescription');
  private readonly tEmptyDescriptionCategory = translateSignal('layout.search.emptyDescriptionCategory');

  /** What the server response modal shows; `null` keeps it closed. */
  readonly detailResponse = computed<ServerResponseData | null>(() => {
    const notif = this.notifications.detailNotif();
    return notif ? notificationToResponse(notif) : null;
  });
  /** Modal title: the notification's own title for client-side messages, otherwise a fixed label. */
  readonly detailTitle = computed<string>(() => {
    const notif = this.notifications.detailNotif();
    return notif && notif.statusCode === undefined ? notif.title : this.tServerResponse();
  });

  /** Bell badge: unread server notifications plus unseen What's New releases. */
  readonly bellCount = computed(() => this.notifications.unreadCount() + this.whatsNew.unseenCount());
  readonly isLoading = signal(false);
  readonly adminHubOpen = signal(false);

  readonly user = computed(() => ({
    name: this.auth.userName(),
    email: this.auth.userEmail(),
    role: this.auth.userRole() as UserRole,
  }));

  readonly featureName = this.routeMetadata.getFeatureName.bind(this.routeMetadata);
  readonly featureIcon = this.routeMetadata.getFeatureIcon.bind(this.routeMetadata);
  readonly featureId = this.routeMetadata.getFeatureId.bind(this.routeMetadata);

  constructor() {
    if (this.viewportIsCompact) {
      this.sidebarExpandedStateBeforeCompact = this.layout.sidebarCollapsed();
      this.layout.sidebarCollapsed.set(true);
    }
    // Another screen asked to open the chat with a draft (see LeapBotChatService.openWithContext).
    effect(() => {
      if (this.leapbot.openRequests() > 0) untracked(() => this.openChat());
    });
  }

  onViewportResize(): void {
    const isCompact = window.innerWidth < 1024;
    if (isCompact && !this.viewportIsCompact) {
      this.sidebarExpandedStateBeforeCompact = this.layout.sidebarCollapsed();
      this.layout.sidebarCollapsed.set(true);
    } else if (!isCompact && this.viewportIsCompact) {
      if (this.sidebarExpandedStateBeforeCompact !== null) {
        this.layout.sidebarCollapsed.set(this.sidebarExpandedStateBeforeCompact);
        this.sidebarExpandedStateBeforeCompact = null;
      }
    }
    this.viewportIsCompact = isCompact;
  }

  /** The category the search modal is scoped to, when opened from a sidebar category click. */
  readonly searchCategory = computed(() => {
    const id = this.search.categoryFilter();
    if (!id) return null;
    return this.toolRegistry.categories().find(cat => cat.id === id) ?? null;
  });

  readonly searchModalTitle = computed(() =>
    this.searchCategory()?.name ?? this.tSearchTitle(),
  );

  readonly searchModalSubtitle = computed(() =>
    this.searchCategory()
      ? this.tSearchSubtitleCategory()
      : this.tSearchSubtitle(),
  );

  readonly searchModalTabs = computed(() =>
    this.searchCategory() ? [] : this.search.tabs,
  );

  readonly searchModalPlaceholder = computed(() =>
    this.searchCategory() ? this.tSearchPlaceholderCategory() : this.tSearchPlaceholder(),
  );

  readonly searchModalResultLabel = computed(() => (this.searchCategory() ? this.tResultTool() : this.tResult()));
  readonly searchModalResultsLabel = computed(() => (this.searchCategory() ? this.tResultToolPlural() : this.tResultPlural()));

  readonly searchModalEmptyTitle = computed(() =>
    this.searchCategory() ? this.tEmptyTitleCategory() : this.tEmptyTitle(),
  );

  readonly searchModalEmptyDescription = computed(() =>
    this.searchCategory()
      ? this.tEmptyDescriptionCategory()
      : this.tEmptyDescription(),
  );

  /**
   * Opens the LeapBot chat window.
   *
   * Focus goes back, when the chat closes, to the element that opened it, or to the floating button
   * when that element is gone (the quick menu entry disappears with the menu).
   */
  openChat(): void {
    if (this.chatOpen() || !this.leapbot.isEnabled()) return;
    this.restoreChatFocus = captureFocus(() => document.querySelector<HTMLElement>('[data-leapbot-fab]'));
    this.chatOpen.set(true);
  }

  /** Opens the chat with the server response on screen attached as a file, plus where the user was. */
  askLeapBotAboutResponse(): void {
    const response = this.detailResponse();
    if (!response) return;
    const file = new File([JSON.stringify(redactSensitive(response), null, 2)], 'server-response.json', {
      type: 'application/json',
    });
    const status = response.status ? ` (HTTP ${response.status})` : '';
    this.leapbot.openWithContext({
      message: `Necesito ayuda con esta respuesta del servidor${status}.`,
      files: [file],
      includeContext: true,
    });
    this.notifications.closeDetail();
  }

  /** Closes the LeapBot chat and gives focus back to the element that opened it. */
  closeChat(): void {
    this.leapbot.cancelRecording();
    this.chatOpen.set(false);
    this.restoreChatFocus?.();
    this.restoreChatFocus = null;
  }

  onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.openSearch();
    }
  }

  openSearch(): void {
    this.search.open();
  }

  onSearch(query: string): void {
    this.search.setQuery(query);
  }

  onSearchTabChange(tab: string): void {
    this.search.setActiveTab(tab);
  }

  onSearchSuggestionSelect(suggestion: SearchSuggestion): void {
    const item = this.search.getItemBySuggestion(suggestion);
    if (item) {
      this.openSearchItem(item);
    }
  }

  onSearchModalItemSelect(item: SearchModalItem): void {
    this.openSearchItem(item);
  }

  toggleSearchItemFavorite(item: SearchModalItem): void {
    this.favorites.toggleFavorite(item.sourceId ?? item.id);
  }

  openSearchItem(item: SearchModalItem): void {
    this.search.close();

    if (!item.route || item.route === '/' || !this.routeMetadata.isRouteAvailable(item.route.slice(1))) {
      return;
    }

    void this.router.navigateByUrl(item.route);
  }

  onCategoryClick(id: string): void {
    if (id === 'admin') {
      this.adminHubOpen.set(true);
      return;
    }

    if (id === PINNED_SIDEBAR_TOOL.id) {
      void this.router.navigateByUrl(PINNED_SIDEBAR_TOOL.route);
      return;
    }

    const categories = this.toolRegistry.categories();

    // Category IDs take priority: a click on a category row always opens search scoped to
    // it, even when a tool inside that category shares the same ID (e.g. 'client-config').
    if (categories.some(cat => cat.id === id)) {
      this.search.openFromCategory(id);
      return;
    }

    // Not a category — it's a tool click (e.g. from favorites). Navigate directly.
    for (const cat of categories) {
      const tool = cat.tools.find(t => t.id === id);
      if (tool) {
        void this.router.navigateByUrl(`/${cat.id}/${toolPathSegment(tool)}`);
        return;
      }
    }
  }

  onAdminCardSelect(cardId: string): void {
    const routes: Record<string, string> = {
      'tool-management': 'admin/tools',
      'category-management': 'admin/categories',
      'client-control': 'admin/clients',
      'toolbelt-schema-management': 'admin/toolbelt-schemas',
      'schema-change-requests': 'admin/schema-change-requests',
      'role-management': 'admin/roles',
      'user-management': 'admin/users',
    };
    const route = routes[cardId];
    if (route) {
      this.adminHubOpen.set(false);
      void this.router.navigateByUrl(route);
    }
  }

  onUserMenuAction(action: UserMenuAction): void {
    if (action === 'logout') {
      this.auth.logout();
    } else if (action === 'language') {
      this.languageOpen.set(true);
    }
  }
}
